<template>
  <div class="sidebar bg-light border-end">
    <BListGroup flush class="sidebar-scroll">
      <!-- 首頁 -->
      <BListGroupItem to="/home" :active="$route.path === '/home'" class="d-flex align-items-center">
        <i class="bi bi-house me-2"></i> 首頁
      </BListGroupItem>

      <!-- 期貨選單 -->
      <BListGroupItem class="menu-header" @click="toggleSection('futures')">
        <i class="bi bi-clock-history me-1"></i> 期貨選單
        <i :class="['bi', 'float-end', openSections.futures ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
      </BListGroupItem>
      <template v-if="openSections.futures">
        <BListGroupItem
          v-for="item in futures"
          :key="item.code"
          @click="handleItemClick(item.code)"
          class="custom-link ps-4"
        >
          {{ item.name }}
        </BListGroupItem>
      </template>

      <!-- 股票多單 -->
      <BListGroupItem class="menu-header" @click="toggleSection('long')">
        <i class="bi bi-arrow-up-circle me-1 text-danger"></i> 股票多單
        <span class="badge bg-danger float-end me-2">{{ stocksLong.length }}</span>
        <i :class="['bi', openSections.long ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
      </BListGroupItem>
      <template v-if="openSections.long">
        <BListGroupItem
          v-for="stock in stocksLong"
          :key="stock.code"
          @click="handleItemClick(stock.code)"
          class="custom-link stock-item"
          :style="{ backgroundColor: stock.color || 'transparent' }"
          :title="stock.stock_name + ' (' + stock.code + ')'"
        >
          <span class="stock-code">{{ stock.code }}</span>
          <span class="stock-name">{{ stock.stock_name.slice(0, 3) }}</span>
        </BListGroupItem>
      </template>

      <!-- 股票空單 -->
      <BListGroupItem class="menu-header" @click="toggleSection('short')">
        <i class="bi bi-arrow-down-circle me-1 text-primary"></i> 股票空單
        <span class="badge bg-primary float-end me-2">{{ stocksShort.length }}</span>
        <i :class="['bi', openSections.short ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
      </BListGroupItem>
      <template v-if="openSections.short">
        <BListGroupItem
          v-for="stock in stocksShort"
          :key="stock.code"
          @click="handleItemClick(stock.code)"
          class="custom-link stock-item"
          :style="{ backgroundColor: stock.color || 'transparent' }"
          :title="stock.stock_name + ' (' + stock.code + ')'"
        >
          <span class="stock-code">{{ stock.code }}</span>
          <span class="stock-name">{{ stock.stock_name.slice(0, 3) }}</span>
        </BListGroupItem>
      </template>

      <!-- 股票觀望 -->
      <BListGroupItem class="menu-header" @click="toggleSection('watch')">
        <i class="bi bi-eye me-1 text-secondary"></i> 股票觀望
        <span class="badge bg-secondary float-end me-2">{{ stocksWatch.length }}</span>
        <i :class="['bi', openSections.watch ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
      </BListGroupItem>
      <template v-if="openSections.watch">
        <BListGroupItem
          v-for="stock in stocksWatch"
          :key="stock.code"
          @click="handleItemClick(stock.code)"
          class="custom-link stock-item"
          :style="{ backgroundColor: stock.color || 'transparent' }"
          :title="stock.stock_name + ' (' + stock.code + ')'"
        >
          <span class="stock-code">{{ stock.code }}</span>
          <span class="stock-name">{{ stock.stock_name.slice(0, 3) }}</span>
        </BListGroupItem>
      </template>

      <!-- 觀察股 -->
      <BListGroupItem class="menu-header" @click="toggleSection('observe')">
        <i class="bi bi-binoculars me-1 text-warning"></i> 觀察股
        <span class="badge bg-warning text-dark float-end me-2">{{ stocksObserve.length }}</span>
        <i :class="['bi', openSections.observe ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
      </BListGroupItem>
      <template v-if="openSections.observe">
        <BListGroupItem
          v-for="stock in stocksObserve"
          :key="stock.code"
          @click="handleItemClick(stock.code)"
          class="custom-link stock-item"
          :style="{ backgroundColor: stock.color || 'transparent' }"
          :title="stock.stock_name + ' (' + stock.code + ')'"
        >
          <span class="stock-code">{{ stock.code }}</span>
          <span class="stock-name">{{ stock.stock_name.slice(0, 3) }}</span>
        </BListGroupItem>
      </template>

    </BListGroup>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { BListGroup, BListGroupItem } from 'bootstrap-vue-next';
import { getTrade } from '@/api/tradeCode.js';
import { useRouter, useRoute } from 'vue-router';

const stocks = ref([]);
const futures = ref([
  { name: '1分鐘', code: 'min1' },
  { name: '3分鐘', code: 'min3' },
  { name: '5分鐘', code: 'min5' },
  { name: '15分鐘', code: 'min15' },
  { name: '1小時', code: 'hour1' },
]);

// 各區塊展開狀態：期貨/空單/觀望預設收起，多單預設展開
const openSections = ref({ futures: false, long: true, short: false, watch: false, observe: false });

function toggleSection(key) {
  openSections.value[key] = !openSections.value[key];
}

// 依 status 分組：多單(1,2)、空單(-1,-2)、觀望(0)，排除前兩碼為 00 的股票
const stocksLong    = computed(() => stocks.value.filter(s => (s.status === 1 || s.status === 2) && !String(s.code).startsWith('00')));
const stocksShort   = computed(() => stocks.value.filter(s => (s.status === -1 || s.status === -2) && !String(s.code).startsWith('00')));
const stocksWatch   = computed(() => stocks.value.filter(s => s.status === 0 && !String(s.code).startsWith('00')));
const stocksObserve = computed(() => stocks.value.filter(s => s.hold > 0 && !String(s.code).startsWith('00')));

const router = useRouter();
const route = useRoute();

async function fetchStocks() {
  try {
    const response = await getTrade();
    if (response.status === 200) {
      stocks.value = response.data;
    } else {
      console.error('Failed to fetch stocks:', response.message);
    }
  } catch (error) {
    console.error('Error fetching stocks:', error);
  }
}

function handleItemClick(code) {
  if (route.params.code !== code) {
    router.replace({ name: 'Chart', params: { code } });
  }
}

onMounted(() => {
  fetchStocks();
});
</script>

<style scoped>
.sidebar {
  width: 80px;
  min-width: 80px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  font-size: 11px;
  border-left: 1px solid #dee2e6;
  background: #f8f9fa;
  flex-shrink: 0;
}

.sidebar-scroll {
  /* 讓列表自然撐高，由 .sidebar 控制滾動 */
}

.menu-header {
  cursor: pointer;
  font-weight: 600;
  user-select: none;
  padding: 0.3rem 0.25rem;
  font-size: 11px;
  text-align: center;
  line-height: 1.2;
}

.menu-header:hover {
  background-color: #e9ecef;
}

.custom-link {
  cursor: pointer;
  color: #000;
  padding: 0.2rem 0.25rem;
  transition: background-color 0.2s ease;
}

.custom-link:hover {
  background-color: #f8f9fa;
  color: #007bff;
}

.stock-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1px;
}

.stock-code {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
}

.stock-name {
  font-size: 10px;
  color: #888;
  line-height: 1.2;
}
</style>
